import React, { useState, useCallback, useMemo } from 'react';
import { Box, createStyles, WithStyles, withStyles } from '@material-ui/core';
import { getConfig } from 'config/config';

import { deleteDocument, statisticDocuments } from 'api/documents';
import DefaultModal from 'components/Shared/DefaultModal';
import Button from 'components/UI/Button';
import { useLanguage } from 'languages/languageContext';
import { downloadFile } from '../../../../utilities/downloadFile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/shadcn/Dropdown';
import toast from 'react-hot-toast';
import { DocumentType } from './DocumentItemTypes';
import { getFileExtension } from 'utilities/getFileExtension';
import { FileIcon } from './FileIcon';
import { RenderFileView } from './RenderFileView';
import { MoveDocumentModal } from '../MoveDocumentModal';

const { theme } = getConfig();
const styles = createStyles({
  container: {
    flex: 1,
    position: 'relative',
    width: '95%',
    alignItems: 'center',
    margin: '0 auto',
    marginBottom: 10,
    alignSelf: 'center',
    border: '1px solid',
    borderRadius: 10,
    padding: 10,
    borderColor: theme.BACKGROUND_SECONDARY,
  },
  fileWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    cursor: 'pointer',
    borderColor: theme.BACKGROUND_SECONDARY,
  },
  iconWrapper: {
    marginRight: 20,
    display: 'flex',
  },
  title: {
    flex: 5,
    marginRight: 25,
  },
  uploaderName: {
    fontSize: 14,
    fontFamily: 'Roboto',
  },
  date: {
    fontSize: 14,
    fontFamily: 'Roboto',
    fontStyle: 'italic',
  },
  fileName: {
    fontSize: 14,
    wordBreak: 'break-word',
    fontFamily: 'Roboto',
  },
  threeDots: {
    height: 45,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 10px',
    width: 50,
    borderRadius: 50,
    flexDirection: 'column',
    display: 'flex',
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 6,
    backgroundColor: theme.BUTTON_SECONDARY,
  },
  buttonText: {
    fontSize: 16,
  },
});

interface Props extends WithStyles<typeof styles> {
  document: any;
  handleDeleteDocument: (id: string) => void;
  showFolderModal: (id: string, title: string) => void;
  showDeleteButton: boolean;
  owner?: boolean;
  setShowLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  onMoveDocument?: () => void;
  currentFolderId?: string | null;
  isFolderAdmin?: boolean;
}

const DocumentItem: React.FC<Props> = (props) => {
  const {
    classes,
    document,
    handleDeleteDocument,
    showFolderModal,
    showDeleteButton,
    owner,
    setShowLoading,
    onMoveDocument,
    currentFolderId,
    isFolderAdmin,
  } = props;
  const { downloads, eventLabels, documentsGallery } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);

  const fileExtention = useMemo(() => getFileExtension(document), [document]);

  const validDocument = useMemo(
    () => ({
      uploader: document.uploader?.name && document.uploader?.lastname ? document.uploader : null,
      filename: document.filename || 'Unknown',
    }),
    [document],
  );

  const showFormattedDate = useCallback((document: DocumentType) => {
    const date = new Date(document.createdAt);
    return date.toLocaleDateString();
  }, []);

  const deleteDocumentHandler = useCallback(() => {
    deleteDocument(document._id).then(() => {
      handleDeleteDocument(document._id);
    });
    setShowDeleteModal(false);
  }, [document._id, handleDeleteDocument]);

  const onCloseCurrent = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleMoveSuccess = useCallback(() => {
    if (onMoveDocument) {
      onMoveDocument();
    }
  }, [onMoveDocument]);

  const downloadFileHandler = useCallback(async () => {
    setShowLoading?.(true);
    downloadFile(document.path, document.filename, fileExtention === 'pdf', false).finally(() =>
      setShowLoading?.(false),
    );
    await statisticDocuments(document?._id, 'downloaded');
  }, [document, fileExtention, setShowLoading]);

  const forceDownloadHandler = useCallback(async () => {
    setShowLoading?.(true);
    downloadFile(document.path, document.filename, false, true).finally(() => setShowLoading?.(false));
    await statisticDocuments(document?._id, 'downloaded');
  }, [document, setShowLoading]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(document.path);
      toast.success('Link kopiert');
    } catch (err: any) {
      console.log('Error copy link ' + err?.message);
    }
  }, [document.path]);

  return (
    <React.Fragment>
      <div className={classes.container}>
        <div className={classes.fileWrapper}>
          <div
            onClick={async () => {
              if (fileExtention === 'pdf') return window.open(document.path);
              if (fileExtention === 'file') return downloadFileHandler();
              setShowModal(true);
              await statisticDocuments(document._id, 'seen');
            }}
            style={{ height: '100%' }}
            className="w-full flex-row flex"
          >
            <div className={classes.iconWrapper}>
              <FileIcon fileExtention={fileExtention} document={document} />
            </div>
            <div className={classes.title}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                {validDocument.uploader && (
                  <span className={classes.uploaderName} style={{ color: theme.TEXT_PRIMARY }}>
                    {validDocument.uploader.name} {validDocument.uploader.lastname}
                  </span>
                )}
                <span className={classes.date} style={{ color: theme.TEXT_SECONDARY }}>
                  {showFormattedDate(document)}
                </span>
              </div>
              <span className={classes.fileName} style={{ color: theme.BUTTON_PRIMARY }}>
                {validDocument.filename}
              </span>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Box className={classes.threeDots}>
                <div className={classes.dot} />
                <div className={classes.dot} />
                <div className={classes.dot} />
              </Box>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="-mt-2 mr-16 sm:mr-12 md:mr-14 lg:mr-28 w-40">
              <DropdownMenuGroup>
                {(owner || isFolderAdmin) && (
                  <>
                    <DropdownMenuItem onClick={() => showFolderModal(document._id, document.filename)}>
                      <span className={classes.buttonText}>{downloads.rename}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowMoveModal(true)}>
                      <span className={classes.buttonText}>{downloads.move}</span>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={forceDownloadHandler}>
                  <span className={classes.buttonText}>{downloads.downloadFile}</span>
                </DropdownMenuItem>
                {(showDeleteButton || owner || isFolderAdmin) && (
                  <DropdownMenuItem
                    onClick={() => {
                      setShowDeleteModal(true);
                    }}
                  >
                    <span className={classes.buttonText}>{downloads.delete}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleCopyLink}>
                  <span className={classes.buttonText}>{eventLabels.copyLinkLabel}</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <RenderFileView
        fileExtention={fileExtention}
        document={document}
        showModal={showModal}
        onClose={onCloseCurrent}
      />
      {showDeleteModal && (
        <DefaultModal
          showModal={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          modalName="deleteDocumentModal"
          modalTitle={documentsGallery.modalTitle}
          message={documentsGallery.modalDesc}
        >
          <Button
            label={documentsGallery.cancel}
            background={theme.BUTTON_SECONDARY}
            onClick={() => {
              setShowDeleteModal(false);
            }}
          />
          <Button label={documentsGallery.delete} onClick={deleteDocumentHandler} />
        </DefaultModal>
      )}
      {showMoveModal && (
        <MoveDocumentModal
          showModal={showMoveModal}
          onClose={() => setShowMoveModal(false)}
          documentId={document._id}
          currentFolderId={currentFolderId}
          onMoveSuccess={handleMoveSuccess}
        />
      )}
    </React.Fragment>
  );
};

export default withStyles(styles)(DocumentItem);
